import React from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, LabelList, Tooltip, CartesianGrid } from 'recharts';
import {
  Box,
  Heading,
  Text,
  useColorModeValue,
  useBreakpointValue,
} from '@chakra-ui/react';

// Interface untuk data kehadiran pemanen
export interface HarvestAttendanceData {
  selected_date: string;
  estate: string;
  jml_pemanen: number;
  jml_fp: number;
}

interface KehadiranPemanenProps {
  data: HarvestAttendanceData[];
}

// Warna berdasarkan threshold kehadiran
const getAttendanceColor = (percentage: number): string => {
  if (percentage >= 90) {
    return '#15803D'; // Hijau tua - kehadiran >= 90%
  } else if (percentage >= 85) {
    return '#84CC16'; // Hijau muda/kuning - kehadiran 85-90%
  } else {
    return '#F59E0B'; // Oranye - kehadiran < 85%
  }
};

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }: any) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  
  if (active && payload?.length) {
    const data = payload[0]?.payload;
    const percentage = data?.percentage || 0;
    const colorLabel = percentage >= 90 ? 'Baik' : percentage >= 85 ? 'Cukup' : 'Kurang';
    
    return (
      <Box 
        bg={bgColor} 
        p={3} 
        borderWidth={1} 
        borderColor={borderColor} 
        borderRadius="md" 
        shadow="md"
      >
        <Text fontWeight="semibold" color={textColor} mb={1}>
          Estate: {data?.estate}
        </Text>
        <Text fontSize="sm" color="gray.600">
          Total Pemanen: <Box as="span" fontWeight="bold">{data?.jml_pemanen?.toLocaleString('id-ID')}</Box>
        </Text>
        <Text fontSize="sm" color="gray.600">
          Hadir (FP): <Box as="span" fontWeight="bold">{data?.jml_fp?.toLocaleString('id-ID')}</Box>
        </Text>
        <Text fontSize="sm" color={data?.color} fontWeight="bold" mt={1}>
          Kehadiran: {percentage}% ({colorLabel})
        </Text>
      </Box>
    );
  }
  return null;
};

const KehadiranPemanen: React.FC<KehadiranPemanenProps> = ({ data }) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const subTextColor = useColorModeValue('gray.600', 'gray.300');

  // Transform data untuk chart dengan persentase
  const chartData = data
    .map((item) => {
      const percentage = item.jml_pemanen > 0 
        ? Math.round((item.jml_fp / item.jml_pemanen) * 100) 
        : 0;
      return {
        estate: item.estate,
        percentage,
        jml_pemanen: item.jml_pemanen,
        jml_fp: item.jml_fp,
        color: getAttendanceColor(percentage),
      };
    })
    // Urutkan dari persentase tertinggi ke terendah
    .sort((a, b) => b.percentage - a.percentage);

  // Responsive values
  const chartMinHeight = useBreakpointValue({
    base: '380px', // mobile
    md: '380px',   // tablet
    lg: '280px',   // laptop
    xl: '240px',   // desktop
    '2xl': '320px' // large desktop
  }) || '280px';

  const rowHeight = useBreakpointValue({ 
    base: '100%',     // mobile
    md: '100%',       // tablet
    lg: '100%',       // laptop
    xl: '100%',       // desktop
    '2xl': '100%'     // large desktop
  });

  const barSize = useBreakpointValue({
    base: 20,
    md: 24,
    lg: 22,
    xl: 20,
    '2xl': 26
  }) || 22;

  const fontSize = useBreakpointValue({
    base: 11,
    md: 12,
    lg: 11,
    xl: 11,
    '2xl': 13
  }) || 11;

  // Custom label untuk menampilkan persentase di ujung bar
  const renderCustomLabel = (props: any) => {
    const { x, y, width, height, value } = props;
    
    return (
      <text
        x={x + width + 8}
        y={y + height / 2}
        fill="#374151"
        fontWeight="bold"
        fontSize={fontSize}
        dominantBaseline="middle"
      >
        {`${value}%`}
      </text>
    );
  };

  return (
    <Box 
      bg={bgColor} 
      borderRadius="lg" 
      p={4} 
      h="100%" 
      display="flex" 
      flexDirection="column"
    >
      {/* Header */}
      <Heading 
        as="h3" 
        size="md" 
        mb={1} 
        color={textColor} 
        fontSize="18px"
      >
        KEHADIRAN PEMANEN (%)
      </Heading>
      
      {/* Subtitle */}
      <Text 
        fontSize="sm" 
        color={subTextColor} 
        mb={3}
      >
        FP / Total Pemanen
      </Text>

      {/* Chart Container */}
      <Box minH={chartMinHeight} h={chartMinHeight} w="100%">
        <ResponsiveContainer width="100%" height={rowHeight}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 50, left: 5, bottom: 5 }}
            barCategoryGap="20%"
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number" 
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
              fontSize={fontSize}
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              type="category" 
              dataKey="estate"
              fontSize={fontSize}
              fontWeight="600"
              tickLine={false}
              axisLine={false}
              width={50}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />
            <Bar 
              dataKey="percentage" 
              barSize={barSize}
              radius={[0, 4, 4, 0]}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.color}
                />
              ))}
              <LabelList 
                dataKey="percentage" 
                content={renderCustomLabel}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default KehadiranPemanen;
